import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart2,
  Zap,
  Target,
  Shield,
  Activity,
  CheckCircle,
  Eye,
  Brain,
  ArrowRight,
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Users,
  Clock,
  Smartphone,
  BellRing,
} from 'lucide-react';

const Home = () => {
  return (
    <div className="relative">
      {/* ================================================================= */}
      {/* 1. HERO                                                           */}
      {/* ================================================================= */}
      <div className="relative min-h-[85vh] flex items-center hero-gradient overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-base via-dark-base to-dark-overlay"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
                <span className="block text-white">El asistente institucional</span>
                <span className="block text-primary mt-2">que analiza el Nasdaq por ti</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-gray-300">
                Detecta setups ICT en tiempo real, envia analisis cuando detecta estructuras tecnicas relevantes
                y registra tu journal automaticamente.
              </p>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <HeroBullet icon={<BarChart2 className="h-5 w-5" />} text="Analisis NQ en tiempo real" />
                <HeroBullet icon={<Target className="h-5 w-5" />} text="Setups con confluencias ICT" />
                <HeroBullet icon={<Shield className="h-5 w-5" />} text="Gestion de riesgo integrada" />
                <HeroBullet icon={<BookOpen className="h-5 w-5" />} text="Journal automatico" />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <Link to="/register" className="btn-primary text-lg px-8 py-3">
                  Probar gratis
                </Link>
                <a href="#como-funciona" className="btn-secondary text-lg px-8 py-3">
                  Ver como funciona
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center mt-4 lg:mt-0">
              <div className="w-full max-w-lg">
                <CandlestickAnimation />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 2. EL PROBLEMA DEL TRADER                                         */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                El problema del trading no es la estrategia.<br />
                <span className="text-primary">Es la disciplina.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <p className="text-lg text-gray-300">La mayoria de traders:</p>
                <PainPoint text="Operan demasiado" />
                <PainPoint text="Entran por emocion" />
                <PainPoint text="No siguen un plan" />
                <PainPoint text="No registran sus operaciones" />
              </div>
              <div className="card p-6 border-red-500/20">
                <p className="text-lg text-gray-200 leading-relaxed">
                  El resultado: <span className="text-red-400 font-semibold">decisiones impulsivas</span> y{' '}
                  <span className="text-red-400 font-semibold">perdidas constantes</span>.
                </p>
                <p className="mt-4 text-gray-400 text-sm">
                  No importa cuanto sepas de ICT si no tienes un sistema que te obligue a ser objetivo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 3. LA SOLUCION                                                    */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Un asistente que analiza el mercado por ti
            </h2>
            <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
              El sistema analiza continuamente el Nasdaq utilizando metodologia ICT
              y te avisa solo cuando se detecta un setup con ventaja estadistica.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-6">
              <div className="card p-5 flex-1 max-w-xs mx-auto border-green-500/20">
                <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-3" />
                <p className="text-white font-medium">No ejecuta operaciones</p>
                <p className="text-gray-400 text-sm mt-1">Tu siempre tienes el control</p>
              </div>
              <div className="card p-5 flex-1 max-w-xs mx-auto border-primary/20">
                <Brain className="h-8 w-8 text-primary mx-auto mb-3" />
                <p className="text-white font-medium">Informacion objetiva</p>
                <p className="text-gray-400 text-sm mt-1">Datos, no emociones</p>
              </div>
              <div className="card p-5 flex-1 max-w-xs mx-auto border-blue-500/20">
                <Target className="h-8 w-8 text-blue-400 mx-auto mb-3" />
                <p className="text-white font-medium">Solo cuando hay edge</p>
                <p className="text-gray-400 text-sm mt-1">Sin ruido, sin overtrading</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 4. COMO FUNCIONA                                                  */}
      {/* ================================================================= */}
      <div className="relative" id="como-funciona">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Como Funciona</h2>
              <p className="mt-4 text-lg text-gray-300">De los datos del mercado a tu analisis en 4 pasos</p>
              <div className="mt-8 inline-flex items-center space-x-3 bg-primary/10 border border-primary/20 rounded-xl px-6 py-3">
                <Clock className="h-5 w-5 text-primary shrink-0" />
                <p className="text-sm sm:text-base text-gray-200">
                  Horario activo: <span className="text-white font-bold">15:30–16:15 y 18:00–20:50 hora española</span>
                  <span className="hidden sm:inline text-gray-400"> · Compatible con tu jornada laboral</span>
                </p>
              </div>
            </div>

            {/* 4 Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              <Step num={1} title="Analiza el mercado" desc="Feed directo de CME Globex. Multiples timeframes: 5m, 15m, 1h, 4h, 1D" icon={<Activity className="h-7 w-7 text-primary" />} />
              <Step num={2} title="Detecta confluencias" desc="Evalua 9 componentes ICT. Solo alerta cuando hay 4+ confluencias alineadas" icon={<Brain className="h-7 w-7 text-primary" />} />
              <Step num={3} title="Envia el analisis" desc="Recibes el analisis de estructura con zonas tecnicas de interes e invalidacion del escenario" icon={<Zap className="h-7 w-7 text-primary" />} />
              <Step num={4} title="Registra el resultado" desc="El journal guarda automaticamente si el setup se activo y cual fue el resultado" icon={<BookOpen className="h-7 w-7 text-primary" />} />
            </div>

            {/* Example Analysis */}
            <div className="max-w-lg mx-auto">
              <p className="text-sm text-gray-400 text-center mb-4">Ejemplo de analisis real</p>
              <div className="card p-5 border-primary/30 bg-dark-card/80">
                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">{`📊 ANÁLISIS NQ — 15:42 🇪🇸

Precio actual: 21,847

🧠 Contexto:
Estructura bajista activa. BOS confirmado en 15m.
RSI 38.4 — momentum descendente.

📍 Zona técnica de interés:
21,850 – 21,878
Order Block sin mitigar con FVG confluente.

📐 Lectura de estructura:
Si el precio retestea esta zona y muestra rechazo,
la estructura es coherente con continuación bajista
hacia las zonas de liquidez inferiores (~21,780).

Un cierre por encima de 21,890 invalidaría
el escenario actual.

⚠️ Análisis técnico automatizado basado en
metodología de estructura de mercado.
No constituye recomendación de inversión.`}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 5. METODOLOGÍA DE VALIDACIÓN                                      */}
      {/* ================================================================= */}
      <div className="relative" id="metodologia">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Metodologia de validacion estadistica</h2>
              <p className="mt-4 text-lg text-gray-300">
                Cada parametro del sistema esta validado antes de operar.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="card p-6 text-center">
                <div className="flex justify-center mb-4">
                  <Eye className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">Walk-Forward Testing</h3>
                <p className="text-sm text-gray-400">
                  Validacion en ventanas temporales independientes fuera de la muestra de entrenamiento.
                </p>
              </div>
              <div className="card p-6 text-center">
                <div className="flex justify-center mb-4">
                  <BarChart2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">Bootstrap & Monte Carlo</h3>
                <p className="text-sm text-gray-400">
                  10,000 simulaciones para verificar robustez estadistica bajo diferentes escenarios de mercado.
                </p>
              </div>
              <div className="card p-6 text-center">
                <div className="flex justify-center mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">Validacion Out-of-Sample</h3>
                <p className="text-sm text-gray-400">
                  El sistema solo se considera valido cuando supera criterios estrictos en datos que nunca ha visto.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 6. QUE INCLUYE LA PLATAFORMA                                      */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Que Incluye</h2>
              <p className="mt-4 text-lg text-gray-300">Todo lo que necesitas en un solo sistema</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <ModuleCard
                icon={<Brain className="h-7 w-7 text-primary" />}
                title="Analisis de Mercado"
                items={['Bias institucional diario', 'Zonas premium / discount', 'Setups con confluencias ICT', 'Deteccion de liquidity sweeps']}
              />
              <ModuleCard
                icon={<Zap className="h-7 w-7 text-yellow-400" />}
                title="Analisis en Tiempo Real"
                items={['Notificaciones en tiempo real', 'Dashboard en vivo', 'Solo cuando hay ventaja', 'Zonas tecnicas e invalidacion del escenario']}
              />
              <ModuleCard
                icon={<Shield className="h-7 w-7 text-blue-400" />}
                title="Gestion de Riesgo"
                items={['Control de balance y MLL', 'Margen disponible', 'Riesgo por operacion', 'Reglas de cuenta fondeada']}
              />
              <ModuleCard
                icon={<BookOpen className="h-7 w-7 text-green-400" />}
                title="Journal Automatico"
                items={['Registro de cada trade', 'Resultado real vs propuesto', 'Analisis de rendimiento', 'Historico completo']}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 7. PARA QUIEN ES                                                  */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Para Quien Es</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <AudienceCard
                icon={<TrendingUp className="h-8 w-8 text-primary" />}
                title="Traders de Nasdaq"
                desc="Operas futuros del NQ o MNQ y quieres un sistema objetivo que te ayude a filtrar setups"
              />
              <AudienceCard
                icon={<Shield className="h-8 w-8 text-blue-400" />}
                title="Cuentas Fondeadas"
                desc="Tienes una cuenta fondeada y necesitas respetar reglas de riesgo estrictas sin fallos"
              />
              <AudienceCard
                icon={<Users className="h-8 w-8 text-green-400" />}
                title="ICT / Smart Money"
                desc="Conoces la metodologia ICT y quieres automatizar la deteccion de confluencias"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 7b. HORARIO — ARGUMENTO DE VENTA CLAVE                           */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-dark-card to-primary/5 p-8 sm:p-12 overflow-hidden">
              {/* Glow decorativo */}
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

              <div className="relative text-center mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mb-5">
                  <Clock className="h-7 w-7 text-primary" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Cuando necesitas estar disponible?
                </h2>
                <p className="mt-3 text-gray-400">Solo necesitas el movil cerca en dos momentos del dia</p>
              </div>

              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
                {/* Ventana 1 */}
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/20 mb-3">
                    <Smartphone className="h-5 w-5 text-blue-400" />
                  </div>
                  <p className="text-2xl font-extrabold text-white tracking-tight">15:30 – 16:15</p>
                  <p className="text-sm text-primary font-semibold mt-1">NY AM Open</p>
                  <p className="text-xs text-gray-500 mt-2">45 minutos</p>
                </div>

                {/* Ventana 2 */}
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/20 mb-3">
                    <Smartphone className="h-5 w-5 text-blue-400" />
                  </div>
                  <p className="text-2xl font-extrabold text-white tracking-tight">18:00 – 20:50</p>
                  <p className="text-sm text-primary font-semibold mt-1">NY Lunch + PM</p>
                  <p className="text-xs text-gray-500 mt-2">Menos de 3 horas</p>
                </div>
              </div>

              <div className="relative rounded-xl bg-white/[0.03] border border-white/10 p-5 text-center space-y-2">
                <div className="flex items-center justify-center space-x-2 text-white">
                  <BellRing className="h-5 w-5 text-yellow-400 shrink-0" />
                  <p className="font-medium">Si llega un analisis, tienes tiempo para revisar la estructura.</p>
                </div>
                <p className="text-sm text-gray-400">
                  Si no llega nada, el dia termino — el mentor no encontro nada valido.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 8. CTA FUERTE                                                     */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-card to-dark-overlay"></div>
        <div className="relative py-20 z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Empieza Hoy</h2>
            <p className="mt-4 text-lg text-gray-300">
              Accede a la plataforma y recibe analisis cuando el sistema detecta estructuras tecnicas relevantes.
            </p>
            <div className="mt-8">
              <Link to="/register" className="btn-primary inline-flex items-center space-x-2 text-lg px-8 py-3">
                <span>Crear Cuenta</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 9. FAQ                                                            */}
      {/* ================================================================= */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-dark-overlay via-dark-base to-dark-overlay"></div>
        <div className="relative py-24 z-10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Preguntas Frecuentes</h2>
            </div>
            <div className="space-y-3">
              <FAQItem
                question="Es un bot automatico?"
                answer="No. El mentor analiza el mercado y envia alertas. Tu decides si operar. Nunca ejecuta operaciones por ti."
              />
              <FAQItem
                question="Funciona con TradingView?"
                answer="Las alertas pueden utilizarse junto a cualquier plataforma de trading. Recibes la informacion en Telegram y en el dashboard web."
              />
              <FAQItem
                question="Que mercado analiza?"
                answer="Actualmente futuros del Nasdaq (NQ y MNQ) en CME Globex."
              />
              <FAQItem
                question="Que es la metodologia ICT?"
                answer="ICT (Inner Circle Trader) es una metodologia de trading institucional que analiza el mercado a traves de 9 componentes: bias diario, liquidity sweeps, order blocks, fair value gaps, displacement, Judas swing, kill zones, estructura de mercado y zonas premium/discount."
              />
              <FAQItem
                question="Como se valida la estrategia?"
                answer="Con walk-forward analysis (5 ventanas de 2 anos de entrenamiento + 1 ano de validacion), bootstrap (10,000 iteraciones) y simulacion Monte Carlo (10,000 permutaciones). Solo se activan configuraciones que superan el baseline en mas del 50% de las ventanas."
              />
              <FAQItem
                question="Necesito experiencia previa?"
                answer="El sistema esta disenado para traders que conocen los futuros del Nasdaq. No es un curso ni un servicio de senales — es una herramienta de analisis profesional."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer is rendered by App.tsx via Footer component */}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const HeroBullet: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex flex-col items-center space-y-2 p-3">
    <div className="text-primary">{icon}</div>
    <p className="text-sm text-gray-300 text-center">{text}</p>
  </div>
);

const PainPoint: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center space-x-3">
    <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
    <p className="text-gray-300">{text}</p>
  </div>
);

const Step: React.FC<{ num: number; title: string; desc: string; icon: React.ReactNode }> = ({ num, title, desc, icon }) => (
  <div className="card p-6 text-center hover:border-primary/20 transition-all duration-300">
    <div className="flex justify-center mb-3">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary text-lg font-bold">
        {num}
      </span>
    </div>
    <div className="flex justify-center mb-3">{icon}</div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-gray-400">{desc}</p>
  </div>
);

const AlertTag: React.FC<{ text: string }> = ({ text }) => (
  <span className="inline-block bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded mr-1">
    {text}
  </span>
);

const StatItem: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <p className="text-2xl md:text-3xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-gray-400 mt-1">{label}</p>
  </div>
);

const ConfigRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-1 border-b border-white/5">
    <span className="text-gray-400">{label}</span>
    <span className="text-white font-medium">{value}</span>
  </div>
);

const ValidationItem: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="flex items-start space-x-3">
    <div className="p-1.5 bg-white/5 rounded shrink-0">{icon}</div>
    <div>
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-gray-400 text-xs">{desc}</p>
    </div>
  </div>
);

const ModuleCard: React.FC<{ icon: React.ReactNode; title: string; items: string[] }> = ({ icon, title, items }) => (
  <div className="card p-6 hover:border-primary/20 transition-all duration-300">
    <div className="flex justify-center mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-white text-center mb-4">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-center space-x-2 text-sm">
          <CheckCircle className="h-3.5 w-3.5 text-green-400 shrink-0" />
          <span className="text-gray-300">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AudienceCard: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="card p-6 text-center hover:border-primary/20 transition-all duration-300">
    <div className="flex justify-center mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-gray-400">{desc}</p>
  </div>
);

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
      >
        <span className="text-white font-medium">{question}</span>
        {open ? (
          <ChevronUp className="h-5 w-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-gray-400 text-sm">{answer}</p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Candlestick Animation
// ---------------------------------------------------------------------------

const CandlestickAnimation: React.FC = () => {
  const [liveClose, setLiveClose] = useState(39);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveClose(prev => {
        const delta = (Math.random() - 0.52) * 2.8;
        return Math.max(32, Math.min(47, prev + delta));
      });
    }, 900);
    return () => clearInterval(interval);
  }, []);

  // Candle data: [open, high, low, close] in pct (0=chart bottom, 100=chart top)
  // Story: rally → liquidity sweep → ICT SHORT activation → systematic drop
  const baseCandles: [number, number, number, number][] = [
    [55, 60, 52, 58],
    [58, 65, 55, 62],
    [62, 68, 58, 65],
    [65, 71, 61, 69],
    [69, 75, 65, 72],
    [72, 91, 69, 86], // LIQUIDITY SWEEP
    [86, 88, 67, 69], // ICT SHORT activation
    [69, 73, 61, 63],
    [63, 68, 57, 59],
    [59, 64, 53, 55],
    [55, 60, 49, 51],
    [51, 56, 45, 48],
    [48, 53, 42, 44],
    [44, 49, 38, 41],
    [41, 45, 35, 39],
  ];

  const VW = 480, VH = 200;
  const padT = 16, padB = 16, padL = 8;
  const chartH = VH - padT - padB;
  const candleW = 13;
  const step = 21;
  const setW = baseCandles.length * step; // 315 px per set

  const toY = (pct: number) => padT + chartH * (1 - pct / 100);

  // Live candle
  const liveOpen = 39;
  const liveHigh = Math.max(liveOpen, liveClose) + 1.8;
  const liveLow  = Math.min(liveOpen, liveClose) - 1.8;
  const liveCol  = liveClose >= liveOpen ? '#22c55e' : '#DC0000';
  const liveX    = VW - candleW - 22;
  const liveCX   = liveX + candleW / 2;
  const liveY    = toY(liveClose);
  const liveBodyTop = toY(Math.max(liveOpen, liveClose));
  const liveBodyH   = Math.max(toY(Math.min(liveOpen, liveClose)) - liveBodyTop, 1);

  // Reference levels
  const entryY  = toY(72);
  const targetY = toY(39);
  const obTop   = toY(76);
  const obBot   = toY(62);

  // Display price (decorative)
  const displayPrice = (20450 + Math.round((liveClose - 39) * 8)).toLocaleString();

  const renderCandle = (c: [number, number, number, number], x: number, key: string) => {
    const [o, h, l, cl] = c;
    const col      = cl >= o ? '#22c55e' : '#DC0000';
    const bodyTop  = toY(Math.max(o, cl));
    const bodyH    = Math.max(toY(Math.min(o, cl)) - bodyTop, 1);
    const cx       = x + candleW / 2;
    return (
      <g key={key}>
        <line x1={cx} y1={toY(h)} x2={cx} y2={toY(l)} stroke={col} strokeWidth={0.8} />
        <rect x={x} y={bodyTop} width={candleW} height={bodyH} fill={col} rx={1} />
      </g>
    );
  };

  return (
    <div className="rounded-xl overflow-hidden border border-white/8 bg-[#0D1017] shadow-2xl">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
        <div className="flex items-center space-x-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-xs text-gray-400 font-mono">NQ · 5m · LIVE</span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-xs font-mono text-red-400 font-semibold">▼ SHORT SETUP</span>
          <span className="text-xs font-mono text-white font-bold">{displayPrice}</span>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative h-36 sm:h-44 lg:h-[200px]">
        <style>{`
          @keyframes scrollCandles {
            from { transform: translateX(0px); }
            to   { transform: translateX(-${setW}px); }
          }
          @keyframes pricePulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.25; }
          }
          @keyframes obGlow {
            0%, 100% { fill-opacity: 0.08; }
            50%       { fill-opacity: 0.14; }
          }
        `}</style>

        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <clipPath id="scrollArea">
              <rect x={0} y={0} width={liveX - 6} height={VH} />
            </clipPath>
            <linearGradient id="leftFade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#0D1017" stopOpacity="1" />
              <stop offset="100%" stopColor="#0D1017" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#0D1017" />
              <stop offset="100%" stopColor="#0F1219" />
            </linearGradient>
          </defs>

          {/* Background */}
          <rect width="100%" height="100%" fill="url(#bgGrad)" />

          {/* Grid lines */}
          {[25, 50, 75].map(pct => (
            <line key={pct} x1={0} y1={toY(pct)} x2={VW} y2={toY(pct)}
              stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
          ))}

          {/* Scrolling candle group */}
          <g clipPath="url(#scrollArea)">
            <g style={{ animation: `scrollCandles ${baseCandles.length * 1.4}s linear infinite` }}>

              {/* ---- First set ---- */}
              {/* OB zone */}
              <rect x={padL} y={obTop} width={6 * step} height={obBot - obTop}
                fill="rgba(245,158,11,0.08)" stroke="rgba(245,158,11,0.22)" strokeWidth={0.8}
                style={{ animation: 'obGlow 3s ease-in-out infinite' }} />
              <text x={padL + 3} y={obTop - 4}
                fill="rgba(245,158,11,0.6)" fontSize={7} fontFamily="monospace" fontWeight="bold">OB</text>

              {/* SWEEP label */}
              <text x={padL + 5 * step} y={toY(93)}
                fill="rgba(220,0,0,0.55)" fontSize={6} fontFamily="monospace">SWEEP</text>

              {/* Candles */}
              {baseCandles.map((c, i) => renderCandle(c, padL + i * step, `a${i}`))}

              {/* ---- Second set (seamless loop) ---- */}
              <rect x={setW + padL} y={obTop} width={6 * step} height={obBot - obTop}
                fill="rgba(245,158,11,0.08)" stroke="rgba(245,158,11,0.22)" strokeWidth={0.8}
                style={{ animation: 'obGlow 3s ease-in-out infinite' }} />
              <text x={setW + padL + 3} y={obTop - 4}
                fill="rgba(245,158,11,0.6)" fontSize={7} fontFamily="monospace" fontWeight="bold">OB</text>
              <text x={setW + padL + 5 * step} y={toY(93)}
                fill="rgba(220,0,0,0.55)" fontSize={6} fontFamily="monospace">SWEEP</text>
              {baseCandles.map((c, i) => renderCandle(c, setW + padL + i * step, `b${i}`))}

            </g>
          </g>

          {/* Left fade mask */}
          <rect x={0} y={0} width={55} height={VH} fill="url(#leftFade)" />

          {/* Vertical separator */}
          <line x1={liveX - 6} y1={padT} x2={liveX - 6} y2={VH - padB}
            stroke="rgba(255,255,255,0.06)" strokeWidth={1} />

          {/* Entry dashed line */}
          <line x1={liveX - 6} y1={entryY} x2={VW} y2={entryY}
            stroke="rgba(220,0,0,0.35)" strokeWidth={1} strokeDasharray="3 3" />
          <text x={liveX + 1} y={entryY - 3}
            fill="rgba(220,0,0,0.55)" fontSize={6.5} fontFamily="monospace">ENTRY</text>

          {/* Target dashed line */}
          <line x1={liveX - 6} y1={targetY} x2={VW} y2={targetY}
            stroke="rgba(34,197,94,0.35)" strokeWidth={1} strokeDasharray="3 3" />
          <text x={liveX + 1} y={targetY - 3}
            fill="rgba(34,197,94,0.55)" fontSize={6.5} fontFamily="monospace">TP</text>

          {/* Live candle */}
          <line x1={liveCX} y1={toY(liveHigh)} x2={liveCX} y2={toY(liveLow)}
            stroke={liveCol} strokeWidth={0.9} />
          <rect x={liveX} y={liveBodyTop} width={candleW} height={liveBodyH}
            fill={liveCol} rx={1} />

          {/* Live price horizontal line */}
          <line x1={liveX - 6} y1={liveY} x2={VW} y2={liveY}
            stroke={liveCol} strokeWidth={1} opacity={0.55}
            style={{ animation: 'pricePulse 1.8s ease-in-out infinite' }} />

          {/* Live price dot */}
          <circle cx={VW - 8} cy={liveY} r={3.5} fill={liveCol}
            style={{ animation: 'pricePulse 1.8s ease-in-out infinite' }} />

        </svg>
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-white/5">
        <span className="text-xs text-gray-600 font-mono hidden sm:inline">ICT · 4+ confluencias · SHORT_ONLY</span>
        <span className="text-xs text-gray-600 font-mono sm:hidden">SHORT_ONLY · ICT</span>
        <span className="text-xs text-gray-600 font-mono">R:R 2.0+</span>
      </div>
    </div>
  );
};

export default Home;
